import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Konva from 'konva';
import { Group, Layer, Line, Stage, Text } from 'react-konva';
import { buildButterflyGeometry } from '../geometry/butterflyGeometry';
import { useElementSize } from '../hooks/useElementSize';
import { BoxDimensions, Point, Viewport } from '../types';
import CanvasToolbar from './CanvasToolbar';
import DimensionGuide from './DimensionGuide';
import DieLineLegend from './DieLineLegend';

type DieLineCanvasProps = {
  dimensions: BoxDimensions;
};

const CANVAS_PADDING = 64;
const MIN_ZOOM = 0.12;
const MAX_ZOOM = 5;
const ZOOM_FACTOR = 1.12;

const flattenPoints = (points: Point[]): number[] =>
  points.flatMap((point) => [point.x, point.y]);

const DieLineCanvas: React.FC<DieLineCanvasProps> = ({ dimensions }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<Konva.Stage>(null);
  const { width, height } = useElementSize(containerRef);
  const geometry = useMemo(
    () => buildButterflyGeometry(dimensions),
    [dimensions]
  );

  const [viewport, setViewport] = useState<Viewport>({
    x: 0,
    y: 0,
    scale: 1,
  });

  const fitToScreen = useCallback(() => {
    if (!width || !height) return;

    const availableWidth = Math.max(1, width - CANVAS_PADDING * 2);
    const availableHeight = Math.max(1, height - CANVAS_PADDING * 2);
    const scale = Math.min(
      availableWidth / geometry.bounds.width,
      availableHeight / geometry.bounds.height,
      2
    );

    setViewport({
      scale,
      x: width / 2 - (geometry.bounds.minX + geometry.bounds.width / 2) * scale,
      y:
        height / 2 -
        (geometry.bounds.minY + geometry.bounds.height / 2) * scale,
    });
  }, [geometry.bounds, height, width]);

  useEffect(() => {
    fitToScreen();
  }, [fitToScreen]);

  const zoomAroundPoint = useCallback(
    (nextScale: number, screenPoint?: Point) => {
      const safeScale = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, nextScale));
      const anchor = screenPoint ?? { x: width / 2, y: height / 2 };
      const modelPoint = {
        x: (anchor.x - viewport.x) / viewport.scale,
        y: (anchor.y - viewport.y) / viewport.scale,
      };

      setViewport({
        scale: safeScale,
        x: anchor.x - modelPoint.x * safeScale,
        y: anchor.y - modelPoint.y * safeScale,
      });
    },
    [height, viewport, width]
  );

  const handleWheel = (event: Konva.KonvaEventObject<WheelEvent>) => {
    event.evt.preventDefault();

    const pointer = stageRef.current?.getPointerPosition();
    if (!pointer) return;

    const direction = event.evt.deltaY > 0 ? 1 / ZOOM_FACTOR : ZOOM_FACTOR;
    zoomAroundPoint(viewport.scale * direction, pointer);
  };

  return (
    <section className="die-line__workspace" ref={containerRef}>
      <DieLineLegend />

      {width > 0 && height > 0 && (
        <Stage
          ref={stageRef}
          width={width}
          height={height}
          x={viewport.x}
          y={viewport.y}
          scaleX={viewport.scale}
          scaleY={viewport.scale}
          draggable
          onWheel={handleWheel}
          onDragEnd={(event) =>
            setViewport((current) => ({
              ...current,
              x: event.target.x(),
              y: event.target.y(),
            }))
          }
        >
          <Layer>
            <Group>
              {/* White panel faces with blue cutting boundaries. */}
              {geometry.panels.map((panel) => (
                <Line
                  key={panel.id}
                  points={flattenPoints(panel.points)}
                  closed
                  fill="#ffffff"
                  stroke="#3248ed"
                  strokeWidth={1.4}
                  strokeScaleEnabled={false}
                  lineJoin="round"
                  listening={false}
                />
              ))}

              {/* Creases are deliberately drawn after cut boundaries. */}
              {geometry.creaseLines.map((line) => (
                <Line
                  key={line.id}
                  points={[line.start.x, line.start.y, line.end.x, line.end.y]}
                  stroke="#f04444"
                  strokeWidth={1.35}
                  strokeScaleEnabled={false}
                  listening={false}
                />
              ))}

              {geometry.dimensionGuides.map((guide) => (
                <DimensionGuide key={guide.id} guide={guide} />
              ))}

              <Text
                x={geometry.bounds.minX}
                y={geometry.bounds.maxY + 14}
                width={geometry.bounds.width}
                text="Drag to pan • Scroll to zoom"
                align="center"
                fill="#788395"
                fontSize={10}
                listening={false}
              />
            </Group>
          </Layer>
        </Stage>
      )}

      <CanvasToolbar
        zoomPercent={Math.round(viewport.scale * 100)}
        onFit={fitToScreen}
        onZoomIn={() => zoomAroundPoint(viewport.scale * ZOOM_FACTOR)}
        onZoomOut={() => zoomAroundPoint(viewport.scale / ZOOM_FACTOR)}
      />
    </section>
  );
};

export default DieLineCanvas;
